import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, useRefresh } from "@/lib/api";
import { PRODUCTS, STATUSES } from "@/lib/crm";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", tax_code: "", birth_date: "", city: "", job: "", income: "", status: "lead", source: "", interests: [], privacy_consent: false, notes: "" };

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>
);

export const ClientFormDialog = ({ open, onOpenChange, client, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const refresh = useRefresh();
  useEffect(() => { if (open) setF(client ? { ...EMPTY, ...client, income: client.income ?? "" } : EMPTY); }, [open, client]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (p) => setF({ ...f, interests: f.interests.includes(p) ? f.interests.filter((x) => x !== p) : [...f.interests, p] });

  const save = async () => {
    if (!f.first_name.trim() || !f.last_name.trim()) return toast.error("Nome e cognome sono obbligatori");
    setSaving(true);
    try {
      const body = { ...f, income: f.income === "" ? null : Number(f.income) };
      const { data } = client ? await api.put(`/clients/${client.client_id}`, body) : await api.post("/clients", body);
      toast.success(client ? "Nominativo aggiornato" : "Nominativo creato");
      refresh();
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{client ? "Modifica nominativo" : "Nuovo nominativo"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Nome *"><Input data-testid="client-first-name" value={f.first_name} onChange={set("first_name")} /></F>
          <F label="Cognome *"><Input data-testid="client-last-name" value={f.last_name} onChange={set("last_name")} /></F>
          <F label="Telefono"><Input data-testid="client-phone" value={f.phone} onChange={set("phone")} placeholder="333 1234567" /></F>
          <F label="Email"><Input data-testid="client-email" type="email" value={f.email} onChange={set("email")} /></F>
          <F label="Codice fiscale"><Input data-testid="client-tax-code" className="font-mono uppercase" value={f.tax_code} onChange={set("tax_code")} /></F>
          <F label="Data di nascita"><Input data-testid="client-birth-date" type="date" value={f.birth_date} onChange={set("birth_date")} /></F>
          <F label="Città"><Input data-testid="client-city" value={f.city} onChange={set("city")} /></F>
          <F label="Professione / Datore di lavoro"><Input data-testid="client-job" value={f.job} onChange={set("job")} placeholder="Dipendente statale, pensionato…" /></F>
          <F label="Reddito netto mensile (€)"><Input data-testid="client-income" type="number" value={f.income} onChange={set("income")} /></F>
          <F label="Fonte contatto"><Input data-testid="client-source" value={f.source} onChange={set("source")} placeholder="Passaparola, Facebook, sito…" /></F>
          <F label="Stato">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="client-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Prodotti di interesse">
            <div className="flex flex-wrap gap-1.5">
              {PRODUCTS.map((p) => (
                <button type="button" key={p.id} data-testid={`client-interest-${p.id}`} onClick={() => toggle(p.id)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${f.interests.includes(p.id) ? "text-white border-transparent" : "text-slate-600 border-slate-200 hover:border-slate-400"}`}
                  style={f.interests.includes(p.id) ? { background: p.color } : {}}>
                  {p.label}
                </button>
              ))}
            </div>
          </F>
          <div className="sm:col-span-2"><F label="Note"><Textarea data-testid="client-notes" rows={3} value={f.notes} onChange={set("notes")} /><p data-testid="client-notes-health-warning" className="text-[11px] text-amber-700">Non inserire nelle note dati sulla salute o altri dati particolari. Le note non vengono inviate all'AI, a meno che tu non lo attivi in Impostazioni.</p></F></div>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-600">
            <Checkbox data-testid="client-privacy" checked={f.privacy_consent} onCheckedChange={(v) => setF({ ...f, privacy_consent: !!v })} />
            Consenso privacy (GDPR) acquisito
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="client-save-btn" onClick={save} disabled={saving} className="bg-[#0F172A] hover:bg-sky-600">{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
